/**
 * Operators — the roster of people who can sign into this panel.
 *
 * This is the screen that decides who may release money, so three things about
 * it are deliberate rather than incidental:
 *
 *   **The server is the gate, not this file.** Every call below is behind
 *   `requireOperator('admin')` on the server, and the server independently
 *   refuses an operator's attempt to change their own role or status and
 *   refuses demoting the last active admin. The controls here disable
 *   themselves for the same cases so nobody clicks into a 403 — but disabling a
 *   button is a courtesy, never the control.
 *
 *   **Suspend, not delete.** There is no delete. An operator's rows in the
 *   append-only audit log point at their id; deleting the account would leave
 *   the record of who approved a payout pointing at nothing. Suspension revokes
 *   every live session immediately and is reversible, which is what "remove
 *   someone's access" should actually mean.
 *
 *   **A password crosses the wire once.** A new or reset password is generated
 *   here, sent once, and held in this component only until the admin dismisses
 *   it. It is never toasted, never logged, never put in a URL, and never handed
 *   to `useAsync`. The server stores only an argon2id hash and returns none of
 *   it back.
 *
 * The one thing this screen deliberately CANNOT do is reset somebody's
 * two-factor. ADR-046 settled that recovery is a box-access seed-script path
 * and that every web-reachable recovery route is a second front door into the
 * account that approves payouts. The panel shows the enrollment state and the
 * exact command instead of offering a button that would weaken the rule.
 */
import { useState, type ReactNode } from 'react';
import {
  MIN_PASSWORD_LENGTH,
  ROLES,
  ROLE_NOTES,
  createOperator,
  fetchOperators,
  suggestPassword,
  updateOperator,
  type OperatorAccount,
  type OperatorRole,
  type OperatorStatus,
} from '../api/operators.ts';
import { fetchAudit, MOCK_MODE } from '../api/client.ts';
import { currentOperator } from '../api/auth.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age } from '../lib/datetime.ts';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  StatusBadge,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import {
  IconAlert,
  IconLock,
  IconPeople,
  IconPulse,
  IconRefresh,
  IconShield,
} from '../ui/icons.tsx';

/** The table's shape, reused by the loading skeleton so nothing jumps. */
const COLUMNS = ['text', 'text', 'text', 'text', 'text', 'num'] as const;

const FILTERS: { id: OperatorStatus | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'suspended', label: 'Suspended' },
];

/** The documented recovery path for a lost authenticator (ADR-046). */
const MFA_RESET_COMMAND = 'pnpm --filter server exec tsx scripts/seed-operator.ts --reset-mfa';

export function Operators(): ReactNode {
  const me = currentOperator();
  const [filter, setFilter] = useState<OperatorStatus | 'all'>('all');
  const [selected, setSelected] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  /** Bumped after every successful mutation, so the roster and the log reload together. */
  const [version, setVersion] = useState(0);

  const roster = useAsync(() => fetchOperators(), [version]);
  const all = roster.data ?? [];
  const rows = filter === 'all' ? all : all.filter((o) => o.status === filter);
  const active = all.find((o) => o.id === selected) ?? null;

  const changed = (): void => {
    setVersion((n) => n + 1);
  };

  // A signed-in operator below admin can reach this by typing the hash — the
  // link is hidden for them. Say so plainly instead of rendering a table of
  // 403s. In mock mode there is no session at all, so this does not apply.
  if (me && me.role !== 'admin') {
    return (
      <Card title="Operators" icon={<IconShield />}>
        <EmptyState
          icon={<IconLock size="lg" />}
          title="This roster is admin only"
          hint={`Your role is ${me.role}. Managing operator accounts is restricted to admins, and the server refuses these calls for any lower role — hiding the screen only saves you the error.`}
        />
      </Card>
    );
  }

  return (
    <div className="split">
      <div className="stack">
        <div className="toolbar">
          <div className="segmented" role="group" aria-label="Operator status">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`segment${filter === f.id ? ' is-active' : ''}`}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="card__spacer" />
          <button
            type="button"
            className={`btn btn--sm ${creating ? 'btn--ghost' : 'btn--primary'}`}
            onClick={() => {
              setCreating((open) => !open);
              setSelected(null);
            }}
          >
            <IconPeople size="sm" />
            {creating ? 'Cancel' : 'New operator'}
          </button>
          <button type="button" className="btn btn--sm btn--ghost" onClick={roster.reload}>
            <IconRefresh size="sm" />
            Refresh
          </button>
        </div>

        <Card
          title="Operators"
          sub="Everyone who can sign into this panel. A role is what they may do; a suspension takes effect on their next request"
          icon={<IconShield />}
          count={rows.length}
          flush
        >
          {roster.error && all.length === 0 ? (
            <ErrorState
              title="The operator roster did not load"
              detail={roster.error}
              onRetry={roster.reload}
            />
          ) : roster.loading && all.length === 0 ? (
            <TableSkeleton columns={COLUMNS} rows={4} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<IconShield size="lg" />}
              title={
                MOCK_MODE
                  ? 'The roster is not mocked'
                  : filter === 'all'
                    ? 'No operator accounts'
                    : `No operator is currently ${filter}`
              }
              hint={
                MOCK_MODE
                  ? 'Real operator accounts are never invented for a fixture — a made-up roster teaches you to trust names that do not exist. Run the panel against the API to see the real one.'
                  : filter === 'all'
                    ? 'The first admin is created by the seed script on the server. Every account after that is created here.'
                    : 'Switch the filter to All to see the rest of the roster.'
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Operator</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Two-factor</th>
                    <th className="right">Last sign-in</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`row--clickable${row.id === selected ? ' is-selected' : ''}`}
                      onClick={() => {
                        setSelected(row.id);
                        setCreating(false);
                      }}
                    >
                      <td className="mono dim">{row.id}</td>
                      <td className="nowrap cell-lead">
                        {row.username}
                        {me && me.id === row.id ? ' · you' : ''}
                        <span className="cell-sub">{row.email}</span>
                      </td>
                      <td>
                        <RoleBadge role={row.role} />
                      </td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                      <td>
                        {row.totpEnabled ? (
                          <Badge tone="win">on</Badge>
                        ) : (
                          <Badge tone={row.role === 'viewer' ? 'neutral' : 'loss'}>off</Badge>
                        )}
                      </td>
                      <td className="right mono muted" title={absolute(row.lastLoginAt)}>
                        {row.lastLoginAt ? age(row.lastLoginAt) : 'never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <RecentChanges version={version} />
      </div>

      {creating ? (
        <CreatePanel
          onDone={() => {
            setCreating(false);
            changed();
          }}
        />
      ) : (
        <ManagePanel operator={active} onChanged={changed} />
      )}
    </div>
  );
}

/**
 * The role, coloured by how much it can do. `admin` is `info` rather than a
 * success tone: holding it is a fact to notice, not an achievement.
 */
function RoleBadge({ role }: { role: OperatorRole }): ReactNode {
  const tone = role === 'admin' ? 'info' : role === 'approver' ? 'accent' : 'neutral';
  return <Badge tone={tone}>{role}</Badge>;
}

// ------------------------------------------------------------------- manage

function ManagePanel({
  operator,
  onChanged,
}: {
  operator: OperatorAccount | null;
  onChanged: () => void;
}): ReactNode {
  const me = currentOperator();
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState<OperatorRole>('viewer');
  /**
   * A live credential, held for exactly as long as the admin needs to copy it
   * out. Never toasted, never stored, and cleared the moment the panel is
   * dismissed or another operator is selected.
   */
  const [issued, setIssued] = useState<string | null>(null);
  const [shownFor, setShownFor] = useState<number | null>(null);

  if (!operator) {
    return (
      <Card title="Manage" icon={<IconShield />}>
        <EmptyState
          compact
          icon={<IconShield size="lg" />}
          title="No operator selected"
          hint="Pick a row to change a role, suspend an account, or force a password reset. Every one of those signs that operator out of every device immediately."
        />
      </Card>
    );
  }

  // Re-seed the local editors when the selection changes, without an effect:
  // the selected id is the identity of this panel's state.
  if (shownFor !== operator.id) {
    setShownFor(operator.id);
    setRole(operator.role);
    setIssued(null);
    return null;
  }

  /**
   * A `const` alias, so the narrowing above survives into every callback below.
   * TypeScript does not carry a parameter's narrowing into a closure; it does
   * carry a const's, and every control here is a closure.
   */
  const op = operator;
  const isSelf = me !== null && me.id === op.id;
  const facts = [
    { label: 'Operator', value: `${op.username} #${op.id}` },
    { label: 'Email', value: op.email },
    { label: 'Role now', value: op.role },
    { label: 'Status now', value: op.status },
    { label: 'Two-factor', value: op.totpEnabled ? 'enabled' : 'not enrolled' },
  ];

  async function apply(
    patch: { role: OperatorRole } | { status: OperatorStatus } | { password: string },
    spec: {
      title: string;
      body: string;
      confirmLabel: string;
      intent?: 'danger';
      challenge?: { phrase: string; hint: string };
    },
    done: () => void,
  ): Promise<void> {
    if (busy) return;
    const result = await confirm({
      title: spec.title,
      body: spec.body,
      facts,
      confirmLabel: spec.confirmLabel,
      ...(spec.intent ? { intent: spec.intent } : {}),
      ...(spec.challenge ? { challenge: spec.challenge } : {}),
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      await updateOperator(op.id, patch);
      done();
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'The change was refused.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <Card title={op.username} sub={op.email} icon={<IconShield />}>
        <div className="kv kv--spaced">
          <span className="kv__k">Role</span>
          <span className="kv__v">
            <RoleBadge role={op.role} />
          </span>
          <span className="kv__k">Status</span>
          <span className="kv__v">
            <StatusBadge status={op.status} />
          </span>
          <span className="kv__k">Two-factor</span>
          <span className="kv__v">{op.totpEnabled ? 'Enabled' : 'Not enrolled'}</span>
          <span className="kv__k">Last sign-in</span>
          <span className="kv__v" title={absolute(op.lastLoginAt)}>
            {op.lastLoginAt ? age(op.lastLoginAt) : 'never'}
          </span>
          <span className="kv__k">Created</span>
          <span className="kv__v" title={absolute(op.createdAt)}>
            {age(op.createdAt)}
          </span>
        </div>

        {isSelf ? (
          <div className="banner banner--slim banner--warn">
            <span className="banner__icon">
              <IconAlert />
            </span>
            <div className="banner__body">
              <span className="banner__title">This is your own account.</span>
              <span className="banner__text">
                You cannot change your own role or suspend yourself — an account that demotes itself
                cannot undo it, and that is how a panel ends up with nobody who can approve a payout.
                The server refuses it too. Ask another admin.
              </span>
            </div>
          </div>
        ) : null}

        <label className="field">
          <span className="field__label">Role</span>
          <select
            className="select"
            value={role}
            disabled={isSelf || busy}
            onChange={(event) => setRole(event.target.value as OperatorRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <span className="field__hint">{ROLE_NOTES[role]}</span>
        </label>

        <div className="row-actions row-actions--start">
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={isSelf || busy || role === op.role}
            onClick={() =>
              void apply(
                { role },
                {
                  title: `Change ${op.username} to ${role}`,
                  body: `${ROLE_NOTES[role]} The change takes effect on their next request and signs them out of every device now.`,
                  confirmLabel: `Make ${role}`,
                },
                () => toast(`${op.username} is now ${role}.`),
              )
            }
          >
            {role === op.role ? 'Role unchanged' : `Change to ${role}`}
          </button>

          {op.status === 'active' ? (
            <button
              type="button"
              className="btn btn--sm btn--danger"
              disabled={isSelf || busy}
              onClick={() =>
                void apply(
                  { status: 'suspended' },
                  {
                    title: `Suspend ${op.username}`,
                    body: 'Every live session is revoked immediately and they cannot sign in again until reactivated. Nothing they have already done is undone, and the audit log keeps their name on it. This is reversible.',
                    confirmLabel: 'Suspend',
                    intent: 'danger',
                  },
                  () => toast(`${op.username} is suspended and signed out.`),
                )
              }
            >
              <IconLock size="sm" />
              Suspend
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--sm btn--primary"
              disabled={isSelf || busy}
              onClick={() =>
                void apply(
                  { status: 'active' },
                  {
                    title: `Reactivate ${op.username}`,
                    body: 'They can sign in again with their existing password, and with their existing second factor if they have one. Confirm you know who is asking for this.',
                    confirmLabel: 'Reactivate',
                  },
                  () => toast(`${op.username} can sign in again.`),
                )
              }
            >
              Reactivate
            </button>
          )}
        </div>
      </Card>

      <Card title="Force a password reset" icon={<IconLock />}>
        {issued ? (
          <>
            <p className="field__hint field__hint--block">
              This is the only time this password is shown. Give it to {op.username} over a
              channel you trust and have them change it. It is not stored anywhere in this browser
              and the server keeps only a hash.
            </p>
            <pre className="well mono-code">{issued}</pre>
            <div className="row-actions row-actions--start">
              <button
                type="button"
                className="btn btn--sm btn--primary"
                onClick={() => setIssued(null)}
              >
                Done — clear it
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="field__hint field__hint--block">
              A new password is generated here, set on the account, and shown once. Every live
              session of theirs is revoked in the same transaction — a reset that leaves sessions
              alive is not a reset. Use this when someone is locked out or a password may have been
              seen.
            </p>
            {isSelf ? (
              <p className="field__hint field__hint--block">
                This is your own account. Resetting revokes your sessions too, so you will be signed
                out of this tab and will need the new password to get back in — copy it before you
                clear it.
              </p>
            ) : null}
            <div className="row-actions row-actions--start">
              <button
                type="button"
                className="btn btn--sm btn--danger"
                disabled={busy}
                onClick={() => {
                  const password = suggestPassword();
                  void apply(
                    { password },
                    {
                      title: `Reset ${op.username}'s password`,
                      body: 'A new password is generated and set immediately, and every device they are signed in on is signed out. You will see the password once, here, and nowhere else.',
                      confirmLabel: 'Reset the password',
                      intent: 'danger',
                      challenge: {
                        phrase: 'RESET',
                        hint: 'Typed confirmation — their current password stops working the moment you click.',
                      },
                    },
                    () => {
                      setIssued(password);
                      toast(`${op.username}'s password was reset and their sessions revoked.`);
                    },
                  );
                }}
              >
                Generate and set a new password
              </button>
            </div>
          </>
        )}
      </Card>

      <Card title="Two-factor" icon={<IconLock />}>
        <div className="kv kv--spaced">
          <span className="kv__k">Enrolled</span>
          <span className="kv__v">{op.totpEnabled ? 'Yes' : 'No'}</span>
          <span className="kv__k">Required for this role</span>
          <span className="kv__v">{op.role === 'viewer' ? 'No' : 'Yes'}</span>
        </div>
        {!op.totpEnabled && op.role !== 'viewer' ? (
          <div className="banner banner--slim banner--warn">
            <span className="banner__icon">
              <IconAlert />
            </span>
            <div className="banner__body">
              <span className="banner__title">Unprotected privileged account.</span>
              <span className="banner__text">
                Approver and admin accounts must carry a second factor. They enrol it themselves
                from the button in the top bar after signing in; once the grace period is closed
                they cannot sign in without it.
              </span>
            </div>
          </div>
        ) : null}
        <p className="field__hint field__hint--block">
          There is no reset button here, deliberately. A web-reachable way to remove somebody's
          second factor is a second front door into the account that approves payouts, so recovery
          for a lost authenticator runs on the server, by somebody with box access — the machine
          that already holds the sealing key:
        </p>
        <pre className="well mono-code">{`${MFA_RESET_COMMAND} ${op.username}`}</pre>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------- create

function CreatePanel({ onDone }: { onDone: () => void }): ReactNode {
  const confirm = useConfirm();
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OperatorRole>('viewer');
  const [password, setPassword] = useState(() => suggestPassword());
  const [busy, setBusy] = useState(false);
  /** Held only until the admin has passed it on. Same rule as the reset above. */
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null);

  const nameOk = /^[a-zA-Z0-9._-]{3,32}$/.test(username.trim());
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const passwordOk = password.length >= MIN_PASSWORD_LENGTH;
  const ready = nameOk && emailOk && passwordOk && !busy;

  if (created) {
    return (
      <Card title="Operator created" icon={<IconShield />}>
        <p className="field__hint field__hint--block">
          Give <strong>{created.username}</strong> this password over a channel you trust. It is
          shown once and is not recoverable — if it is lost, reset it from their row. They should
          enrol a second factor on their first sign-in.
        </p>
        <pre className="well mono-code">{created.password}</pre>
        <div className="row-actions row-actions--start">
          <button
            type="button"
            className="btn btn--sm btn--primary"
            onClick={() => {
              setCreated(null);
              onDone();
            }}
          >
            Done — clear it
          </button>
        </div>
      </Card>
    );
  }

  async function submit(): Promise<void> {
    if (!ready) return;
    const result = await confirm({
      title: `Create the operator ${username.trim()}`,
      body: 'A new account that can sign into this panel. The password is shown to you once after it is created and never again.',
      facts: [
        { label: 'Username', value: username.trim() },
        { label: 'Email', value: email.trim() },
        { label: 'Role', value: role },
        { label: 'They will be able to', value: ROLE_NOTES[role] },
      ],
      confirmLabel: 'Create operator',
      ...(role === 'admin'
        ? {
            intent: 'danger' as const,
            challenge: {
              phrase: 'ADMIN',
              hint: 'An admin can manage this roster, the platform keys and the house edge.',
            },
          }
        : {}),
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const account = await createOperator({
        username: username.trim(),
        email: email.trim(),
        role,
        password,
      });
      setCreated({ username: account.username, password });
      setUsername('');
      setEmail('');
      setPassword(suggestPassword());
      toast(`${account.username} created as ${account.role}.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'The account was not created.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="New operator" icon={<IconPeople />}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field__label">Username</span>
          <input
            className="input"
            value={username}
            autoFocus
            autoComplete="off"
            placeholder="e.g. priya.desk"
            onChange={(event) => setUsername(event.target.value)}
          />
          {username !== '' && !nameOk ? (
            <span className="field__error">
              3–32 characters: letters, digits, dot, underscore and hyphen.
            </span>
          ) : (
            <span className="field__hint">
              This is what appears against their actions in the audit log, so make it a person.
            </span>
          )}
        </label>

        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="input"
            type="email"
            value={email}
            autoComplete="off"
            placeholder="name@example.com"
            onChange={(event) => setEmail(event.target.value)}
          />
          {email !== '' && !emailOk ? (
            <span className="field__error">That is not an email address.</span>
          ) : null}
        </label>

        <label className="field">
          <span className="field__label">Role</span>
          <select
            className="select"
            value={role}
            onChange={(event) => setRole(event.target.value as OperatorRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <span className="field__hint">{ROLE_NOTES[role]}</span>
        </label>

        <label className="field">
          <span className="field__label">Initial password</span>
          <input
            className="input mono-code"
            value={password}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setPassword(event.target.value)}
          />
          {passwordOk ? (
            <span className="field__hint">
              Generated in the browser from a cryptographic source. Shown once after creation, then
              never again — the server keeps only an argon2id hash.
            </span>
          ) : (
            <span className="field__error">
              At least {MIN_PASSWORD_LENGTH} characters. These accounts release money; the player
              minimum is not the right floor for them.
            </span>
          )}
        </label>

        <div className="row-actions row-actions--start">
          <button type="submit" className="btn btn--sm btn--primary" disabled={!ready}>
            {busy ? 'Creating…' : 'Create operator'}
          </button>
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => setPassword(suggestPassword())}
          >
            <IconRefresh size="sm" />
            New password
          </button>
        </div>
      </form>
    </Card>
  );
}

// ------------------------------------------------------------ recent changes

/**
 * The last few roster changes, read straight from the append-only log rather
 * than kept in a second place. `operator.` catches the whole family — create,
 * update, suspend, the MFA enrollment stamps and the refused sign-ins.
 */
function RecentChanges({ version }: { version: number }): ReactNode {
  const log = useAsync(() => fetchAudit({ action: 'operator.', limit: 12 }), [version]);
  const entries = log.data ?? [];

  return (
    <Card title="Recent roster changes" sub="From the append-only audit log" icon={<IconPulse />} flush>
      {log.error && entries.length === 0 ? (
        <ErrorState title="The log did not load" detail={log.error} onRetry={log.reload} />
      ) : log.loading && entries.length === 0 ? (
        <TableSkeleton columns={['text', 'text', 'text', 'num']} rows={4} />
      ) : entries.length === 0 ? (
        <EmptyState
          compact
          icon={<IconPulse size="lg" />}
          title="Nothing recorded yet"
          hint="Creating an operator, changing a role or suspending an account writes a row here that cannot be edited or deleted."
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Action</th>
                <th>By</th>
                <th>Target</th>
                <th className="right">When</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="nowrap cell-lead">{entry.action}</td>
                  <td className="nowrap muted">{entry.operatorUsername ?? 'system'}</td>
                  <td className="mono dim">
                    {entry.targetId === null ? '—' : `#${entry.targetId}`}
                  </td>
                  <td className="right mono muted" title={absolute(entry.createdAt)}>
                    {age(entry.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
